import React, { useState } from 'react';
import { bitstring } from './quantum/QuantumState';
import { fmtComplex } from './quantum/format';

const phaseColor = (phaseRad) => {
  const deg = (((phaseRad * 180) / Math.PI) % 360 + 360) % 360;
  return `hsl(${deg}, 70%, 50%)`;
};

/**
 * Q-Sphere: every computational basis state of `state` (an n-qubit state
 * vector), laid out around a circle. Dot area encodes probability, dot hue
 * encodes phase, and clicking a state shows its exact amplitude/probability
 * /phase — all read directly from the real state vector, never faked.
 */
export default function QSphere({ state, numQubits, size = 280 }) {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const d = state.length;
  const center = size / 2;
  const radius = size * 0.36;

  const points = Array.from({ length: d }, (_, i) => {
    const amp = state[i];
    const prob = amp.re * amp.re + amp.im * amp.im;
    const phase = Math.atan2(amp.im, amp.re);
    const angle = (i / d) * 2 * Math.PI - Math.PI / 2;
    const x = center + radius * Math.cos(angle);
    const y = center + radius * Math.sin(angle);
    const dotR = 3 + Math.sqrt(prob) * 18;
    return { i, amp, prob, phase, x, y, dotR, label: bitstring(i, numQubits) };
  });

  const selected = selectedIndex != null ? points[selectedIndex] : null;

  return (
    <div>
      <svg width={size} height={size} className="mx-auto block" style={{ maxWidth: '100%' }}>
        <circle cx={center} cy={center} r={radius} fill="none" stroke="#cbd5e1" strokeDasharray="3,3" />
        {points.map((p) => (
          <g key={p.i} onClick={() => setSelectedIndex(p.i)} style={{ cursor: 'pointer' }}>
            <circle
              cx={p.x}
              cy={p.y}
              r={p.dotR}
              fill={p.prob > 1e-6 ? phaseColor(p.phase) : '#e5e7eb'}
              stroke={selectedIndex === p.i ? '#111827' : '#94a3b8'}
              strokeWidth={selectedIndex === p.i ? 2 : 1}
            />
            <text x={p.x} y={p.y - p.dotR - 6} textAnchor="middle" fontSize="10" fill="#374151" fontFamily="monospace">
              |{p.label}⟩
            </text>
          </g>
        ))}
      </svg>
      <div className="mt-2 text-center">
        {selected ? (
          <div className="inline-block bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-left font-mono text-xs space-y-0.5">
            <div className="font-bold text-gray-900">Basis state: |{selected.label}⟩</div>
            <div className="text-gray-600">Amplitude: {fmtComplex(selected.amp)}</div>
            <div className="text-gray-600">Probability: {(selected.prob * 100).toFixed(1)}%</div>
            <div className="text-gray-600">
              Phase: {selected.prob > 1e-6 ? `${((((selected.phase * 180) / Math.PI) % 360 + 360) % 360).toFixed(1)}°` : 'undefined (zero amplitude)'}
            </div>
          </div>
        ) : (
          <p className="text-gray-400 text-xs italic">Click a basis state above to see its amplitude, probability, and phase.</p>
        )}
      </div>
    </div>
  );
}
